import React from 'react';
import useSWR from 'swr';
import api from '../../../services/api';
import { Email } from '../useData';
import { Link } from 'react-router-dom';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { emailDetailPath } from '../utils/emailPartitionAtQuery';

interface LinkedEmailsSectionProps {
  clientId?: number;
}

const LinkedEmailsSection: React.FC<LinkedEmailsSectionProps> = ({ clientId }) => {
  const { colors } = useTheme();

  const queryParams = new URLSearchParams();
  if (clientId) queryParams.append('linkedClientId', clientId.toString());

  const { data: emails, isLoading } = useSWR<{ data: Email[]; total: number }>(
    clientId ? `/emails?${queryParams.toString()}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  if (!clientId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={cn('rounded-lg shadow p-4', colors.surface)}>
        <h3 className={cn('text-lg font-semibold mb-4', colors.text)}>Linked Emails</h3>
        <p className={cn(colors.textMuted)}>Loading...</p>
      </div>
    );
  }

  if (!emails || emails.data.length === 0) {
    return (
      <div className={cn('rounded-lg shadow p-4', colors.surface)}>
        <h3 className={cn('text-lg font-semibold mb-4', colors.text)}>Linked Emails</h3>
        <p className={cn('text-sm', colors.textMuted)}>No linked emails</p>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg shadow p-4', colors.surface)}>
      <h3 className={cn('text-lg font-semibold mb-4', colors.text)}>Linked Emails ({emails.total})</h3>
      <div className="space-y-2">
        {emails.data.map((email) => {
          const from = Array.isArray(email.from) ? email.from[0] : email.from;
          return (
            <div
              key={email.partitionAt ? `${email.id}-${email.partitionAt}` : email.id}
              className={cn('border rounded p-3 transition-colors', colors.border, colors.interactiveHover)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Link
                    to={emailDetailPath(email.id, email.partitionAt)}
                    className={cn('font-medium hover:underline', colors.primaryFg)}
                  >
                    {email.subject || '(no subject)'}
                  </Link>
                  <p className={cn('text-sm mt-1', colors.textSecondary)}>
                    From: {from?.name || from?.email || 'Unknown'}
                  </p>
                  <p className={cn('text-xs mt-1', colors.textMuted)}>
                    {formatDateGlobal(email.date)}
                  </p>
                </div>
                {!email.isRead && (
                  <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full" title="Unread" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LinkedEmailsSection;
